/* Iter 418.bi (Dirk, 2026-02-28) — Full announcement manager panel,
   used as the "Announcements" tab body on CustomerDetail and rendered
   inline on the customer portal so clients can see history + verify
   receipts. Extracted from the old CustomerAnnouncementButton modal so
   both places share one code path. */

import { useEffect, useState } from "react";
import { Megaphone, Loader2, Check, Trash2, Pencil, Users } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import DictTextarea from "../DictTextarea";

export default function CustomerAnnouncementsSection({ customer, readOnly = false }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState({});
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("info");
  const [requireAck, setRequireAck] = useState(true);
  const [busy, setBusy] = useState(false);
  const [past, setPast] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [acks, setAcks] = useState({});
  const [editingId, setEditingId] = useState(null);
  // Iter 418.bj (Dirk, 2026-02-28) — Composer is collapsible so the
  // mobile view doesn't push History off the bottom. Auto-opens when
  // the user hits "Edit" on a past announcement.
  const [composerOpen, setComposerOpen] = useState(false);

  const load = async () => {
    try {
      // Iter 418.bi (Dirk, 2026-02-28) — Portal (read-only) calls
      // the client-scoped history endpoint; CRM uses the staff list
      // that also returns ack counts + non-me announcements.
      if (readOnly) {
        const { data } = await api.get(`/portal/me/announcements/history`);
        setUsers([]);
        setPast(Array.isArray(data) ? data : (data?.items || []));
      } else {
        const [u, list] = await Promise.all([
          api.get(`/customers/${customer.id}/portal-users`),
          api.get(`/portal-announcements?customer_id=${customer.id}`),
        ]);
        setUsers(Array.isArray(u.data) ? u.data : (u.data.items || []));
        setPast(Array.isArray(list.data) ? list.data : []);
      }
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [customer?.id]);

  const submit = async () => {
    const targetIds = Object.keys(selected).filter((k) => selected[k]);
    if (!title.trim() || !body.trim()) { toast.error("Title and body are required"); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/portal-announcements/${editingId}`, {
          title: title.trim(),
          body: body.trim(),
          priority,
          require_ack: requireAck,
        });
        toast.success("Announcement updated");
      } else {
        await api.post("/portal-announcements", {
          customer_id: customer.id,
          title: title.trim(),
          body: body.trim(),
          priority,
          target_user_ids: targetIds,
          require_ack: requireAck,
        });
        toast.success("Announcement posted");
      }
      setTitle(""); setBody(""); setSelected({}); setEditingId(null);
      setPriority("info"); setRequireAck(true);
      setComposerOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const beginEdit = (a) => {
    setEditingId(a.id);
    setTitle(a.title || "");
    setBody(a.body || "");
    setPriority(a.priority || "info");
    setRequireAck(a.require_ack !== false);
    setComposerOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setTitle(""); setBody(""); setPriority("info"); setRequireAck(true);
    setComposerOpen(false);
  };

  const loadAcks = async (aid) => {
    if (expanded === aid) { setExpanded(null); return; }
    setExpanded(aid);
    try {
      const { data } = await api.get(`/portal-announcements/${aid}/acks`);
      setAcks((prev) => ({ ...prev, [aid]: data.users || [] }));
    } catch { /* silent */ }
  };

  const archive = async (aid) => {
    if (!window.confirm("Archive this announcement?")) return;
    try {
      await api.delete(`/portal-announcements/${aid}`);
      load();
    } catch { /* silent */ }
  };

  return (
    <div className="space-y-4" data-testid="customer-announcements-section">
      {!readOnly && !composerOpen && (
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          className="hv-btn hv-btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
          data-testid="announcement-open-composer"
        >
          <Megaphone className="w-4 h-4" /> New announcement
        </button>
      )}
      {!readOnly && composerOpen && (
        <div className="hv-card p-4 space-y-3" data-testid="announcement-composer">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display text-lg font-bold flex-1">
              {editingId ? "Edit announcement" : "Post an announcement"}
            </h3>
            <button
              type="button"
              onClick={cancelEdit}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="announcement-close-composer"
            >
              {editingId ? "Cancel edit" : "Close"}
            </button>
          </div>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Announcement title"
            className="hv-input w-full text-sm"
            data-testid="announcement-title"
            maxLength={120}
          />
          <DictTextarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Body — what should the customer know / do?"
            className="hv-input w-full text-sm min-h-[110px]"
            data-testid="announcement-body"
          />
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-1">
              Priority:
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="hv-input text-xs">
                <option value="info">Info</option>
                <option value="warn">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={requireAck}
                onChange={(e) => setRequireAck(e.target.checked)}
                data-testid="announcement-require-ack"
              />
              Require acknowledgement
            </label>
          </div>
          {!editingId && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1 inline-flex items-center gap-1">
                <Users className="w-3 h-3" /> Target users ({Object.values(selected).filter(Boolean).length || "all"} selected)
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 rounded border border-white/5 p-2">
                {users.length === 0 && <div className="text-xs text-[#647087]">No portal users yet.</div>}
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm min-w-0">
                    <input
                      type="checkbox"
                      checked={!!selected[u.id]}
                      onChange={(e) => setSelected((p) => ({ ...p, [u.id]: e.target.checked }))}
                      data-testid={`announcement-target-${u.id}`}
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {u.name}
                      <span className="text-[10px] text-[#647087] ml-1">· {u.email}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-[#647087] mt-1">Leave all unchecked to blast every portal user.</div>
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={busy || !title.trim() || !body.trim()}
            className="hv-btn hv-btn-primary w-full"
            data-testid="announcement-submit"
          >
            {busy
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
              : <><Megaphone className="w-3 h-3" /> {editingId ? "Save changes" : "Post announcement"}</>}
          </button>
        </div>
      )}

      <div className="hv-card p-4">
        <div className="text-[11px] uppercase tracking-wider text-[#647087] mb-2 inline-flex items-center gap-1">
          <Megaphone className="w-3 h-3" /> {readOnly ? "Portal announcements" : "History"}
        </div>
        <div className="space-y-2">
          {past.length === 0 && (
            <div className="text-sm text-[#a0aabf] italic py-4 text-center">No announcements yet.</div>
          )}
          {past.map((a) => (
            <div key={a.id} className="border border-white/10 rounded-md p-3" data-testid={`announcement-row-${a.id}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate flex items-center gap-2">
                    {a.title}
                    <PriorityChip p={a.priority} />
                  </div>
                  {a.body && <div className="text-xs text-[#a0aabf] mt-1 whitespace-pre-wrap">{a.body}</div>}
                  <div className="text-[10px] text-[#647087] mt-1.5">
                    {a.ack_count} of {a.target_count} acknowledged · {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
                {!readOnly && (
                  <div className="shrink-0 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => loadAcks(a.id)}
                      className="hv-btn hv-btn-ghost text-xs"
                      data-testid={`announcement-who-${a.id}`}
                    >
                      {expanded === a.id ? "Hide" : "Who?"}
                    </button>
                    <button
                      type="button"
                      onClick={() => beginEdit(a)}
                      className="hv-btn hv-btn-ghost text-xs"
                      data-testid={`announcement-edit-${a.id}`}
                      title="Edit announcement"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => archive(a.id)}
                      className="hv-btn hv-btn-ghost text-xs text-rose-300"
                      data-testid={`announcement-archive-${a.id}`}
                      title="Archive announcement"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              {expanded === a.id && !readOnly && (
                <div className="mt-2 pt-2 border-t border-white/5 space-y-1">
                  {(acks[a.id] || []).length === 0 && (
                    <div className="text-[11px] text-[#647087] italic">Loading receipts…</div>
                  )}
                  {(acks[a.id] || []).map((u) => (
                    <div key={u.id} className="text-xs flex items-center gap-2">
                      {u.acked_at
                        ? <Check className="w-3 h-3 text-emerald-400" />
                        : <span className="w-3 h-3 rounded-full bg-amber-400/40 border border-amber-400/60" />}
                      <span className="truncate">{u.name}</span>
                      <span className="text-[10px] text-[#647087] ml-auto">
                        {u.acked_at ? new Date(u.acked_at).toLocaleString() : "pending"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PriorityChip({ p }) {
  const map = {
    info:     { cls: "border-cyan-500/40 bg-cyan-500/10 text-cyan-200",     label: "Info" },
    warn:     { cls: "border-amber-500/40 bg-amber-500/10 text-amber-200",  label: "Warning" },
    critical: { cls: "border-rose-500/40 bg-rose-500/10 text-rose-200",     label: "Critical" },
  };
  const m = map[p] || map.info;
  return (
    <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${m.cls}`}>
      {m.label}
    </span>
  );
}
